import {CalloutIcon} from '@wandb/components';
import * as globals from '@wandb/weave/common/css/globals.styles';
import React, {useContext, useEffect, useMemo, useState} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';

import {Pagination} from '../../Pagination';
import {InstrumentedCenteredWaveLoader as Loader} from '../../utility/InstrumentedLoader';
import {RegistriesSearchContext} from '../common/RegistriesSearchContextProvider';
import {RegistryTypeWithMembership} from './hooks';
import {RegistryCard} from './RegistryCard';

const STARRED_REGISTRIES_PAGE_SIZE = 6;

type StarredRegistriesSectionProps = {
  loading: boolean;
  starredRegistries: RegistryTypeWithMembership[];
  readOnly: boolean;
};

const StarredRegistriesSection = ({
  loading,
  starredRegistries,
  readOnly,
}: StarredRegistriesSectionProps) => {
  const {searchFilter, searchFilterRegex} = useContext(RegistriesSearchContext);
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [starredRegistries, searchFilter]);

  const filteredStarredRegistries = useMemo(() => {
    if (!searchFilterRegex) {
      return starredRegistries;
    }
    return starredRegistries.filter(registry => {
      const nameMatches = searchFilterRegex.test(registry.registryName ?? '');
      const descMatches = searchFilterRegex.test(registry.description ?? '');
      const typeMatches = (registry.types ?? []).some(t =>
        searchFilterRegex.test(t)
      );
      return nameMatches || descMatches || typeMatches;
    });
  }, [starredRegistries, searchFilterRegex]);

  const itemCount = filteredStarredRegistries.length;
  const perPage = STARRED_REGISTRIES_PAGE_SIZE;

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(itemCount / perPage) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [itemCount, perPage, page]);

  const registriesToDisplay = useMemo(() => {
    const start = page * perPage;
    return filteredStarredRegistries.slice(start, start + perPage);
  }, [filteredStarredRegistries, page, perPage]);

  if (loading) {
    return (
      <div className="w-full border-t border-moon-200 pb-32 pt-32">
        <Loader name="global-registry-landing-page-starred" size="huge" />
      </div>
    );
  }

  const showEmptyState =
    !starredRegistries.length && !searchFilter && !readOnly;
  const showNoResults = !!searchFilter && !filteredStarredRegistries.length;

  return (
    <div className="w-full border-t border-moon-200 pb-32 pt-32">
      <div className="flex flex-row items-center justify-between">
        <div className="mb-16 text-base font-semibold text-moon-550">
          My starred registries
        </div>
        {itemCount > perPage && (
          <Pagination
            itemCount={itemCount}
            page={page}
            perPage={perPage}
            onPageChange={setPage}
          />
        )}
      </div>
      <div className="grid grid-cols-1 items-center gap-14 whitespace-nowrap sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
        {showEmptyState ? (
          <div className="col-span-1 sm:col-span-1 md:col-span-2 xl:col-span-3">
            <StarredRegistriesEmptyState />
          </div>
        ) : showNoResults ? (
          <div className="text-base text-moon-550">No registries found</div>
        ) : (
          registriesToDisplay.map(registry => (
            <RegistryCard
              key={registry.registryName}
              {...registry}
              canViewCollectionCount={registry.canViewCollectionCount}
              isRegistryAvailableToJoin={registry.isRegistryAvailableToJoin}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default StarredRegistriesSection;

const StarredRegistriesEmptyState = () => {
  return (
    <EmptyStateContainer>
      <CalloutIcon icon="star" color="gold" />
      <EmptyStateHeader className="font-semibold">
        No starred registries
      </EmptyStateHeader>
      <EmptyStateBody>
        Star a registry and it will always show here for you.
      </EmptyStateBody>
    </EmptyStateContainer>
  );
};

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: ${globals.WHITE};
  border: 1px solid ${globals.MOON_200};
  border-radius: 8px;
  padding: 24px;
`;

const EmptyStateHeader = styled.div`
  font-size: 20px;
  margin-top: 8px;
`;

const EmptyStateBody = styled.div`
  color: ${globals.MOON_550};
  font-size: 16px;
  margin-top: 4px;
`;
